import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import ClassRoundedIcon from "@mui/icons-material/ClassRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.65)",
  border: "1px solid rgba(255,255,255,0.85)",
  boxShadow:
    "0 8px 32px rgba(99,102,241,0.08), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const SelectorSection = ({
  icon,
  label,
  description,
  value,
  onChange,
  options,
  disabled,
  color,
  lightColor,
  gradient,
  placeholder,
  loading,
  onNavigate,
  navigateLabel,
  navigateDisabled,
}) => (
  <MotionBox
    layout
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    sx={{
      p: "18px 20px",
      borderRadius: "18px",
      border: `1px solid ${
        disabled
          ? "rgba(0,0,0,0.05)"
          : value
          ? `${color}30`
          : "rgba(0,0,0,0.08)"
      }`,
      background: disabled
        ? "rgba(249,250,251,0.7)"
        : value
        ? `rgba(${color
            .replace(/[^,]+,\s*/g, "")
            .replace(")", "")
            .split(",")
            .join(",")},0.04)`
        : "rgba(255,255,255,0.5)",
      backdropFilter: "blur(12px)",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* accent line */}
    {!disabled && (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: value ? "100%" : "0%",
          height: 2,
          background: gradient,
          transition: "width 0.5s cubic-bezier(0.22,1,0.36,1)",
          borderRadius: "0 0 2px 0",
        }}
      />
    )}

    <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "11px",
          background: disabled
            ? "rgba(0,0,0,0.04)"
            : value
            ? `${lightColor}`
            : "rgba(0,0,0,0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: disabled ? "#9ca3af" : color,
          "& svg": { fontSize: 18 },
          border: disabled ? "none" : `1px solid ${color}20`,
          transition: "all 0.3s",
        }}
      >
        {disabled ? <LockRoundedIcon /> : icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 700,
            color: disabled ? "#9ca3af" : "#111827",
            fontSize: "0.88rem",
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ color: "#9ca3af", fontSize: "0.7rem", fontWeight: 500 }}
        >
          {description}
        </Typography>
      </Box>
      {value && (
        <Chip
          label="Selected"
          size="small"
          sx={{
            background: `${color}15`,
            color,
            fontWeight: 700,
            fontSize: "0.66rem",
            height: 20,
            border: `1px solid ${color}25`,
            borderRadius: "6px",
          }}
        />
      )}
    </Stack>

    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems="flex-end"
    >
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel
          sx={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: disabled ? "#d1d5db" : "#6b7280",
            "&.Mui-focused": { color },
          }}
        >
          {loading ? "Loading..." : placeholder}
        </InputLabel>
        <Select
          value={value || ""}
          label={placeholder}
          onChange={(e) => onChange(e.target.value)}
          sx={{
            borderRadius: "13px",
            background: disabled
              ? "rgba(249,250,251,0.6)"
              : "rgba(255,255,255,0.7)",
            fontSize: "0.875rem",
            fontWeight: 600,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: disabled
                ? "rgba(0,0,0,0.05)"
                : value
                ? `${color}50`
                : "rgba(0,0,0,0.1)",
              transition: "border-color 0.2s",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: disabled ? undefined : color,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: color,
              borderWidth: 1.5,
            },
          }}
          endAdornment={
            loading ? (
              <CircularProgress size={13} sx={{ color, mr: 1 }} />
            ) : undefined
          }
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {placeholder}
            </Typography>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.id} value={opt.id}>
              <Typography variant="body2" fontWeight={600}>
                {opt.name}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tooltip
        title={
          navigateDisabled
            ? "Please select a value first"
            : `View detailed ${label} analysis`
        }
        arrow
      >
        <span>
          <Button
            variant="contained"
            size="small"
            disabled={navigateDisabled}
            onClick={onNavigate}
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              whiteSpace: "nowrap",
              flexShrink: 0,
              background: navigateDisabled ? undefined : gradient,
              boxShadow: navigateDisabled ? "none" : `0 6px 16px ${color}35`,
              "&:hover": {
                background: navigateDisabled ? undefined : gradient,
                boxShadow: navigateDisabled ? "none" : `0 10px 24px ${color}45`,
                transform: "translateY(-1px)",
              },
              borderRadius: "12px",
              height: 36,
              fontSize: "0.78rem",
              fontWeight: 700,
              transition: "all 0.2s ease",
              fontFamily: "'Syne', sans-serif",
            }}
          >
            {navigateLabel}
          </Button>
        </span>
      </Tooltip>
    </Stack>

    {disabled && (
      <Stack direction="row" alignItems="center" spacing={0.5} mt={1.5}>
        <InfoOutlinedIcon sx={{ fontSize: 12, color: "#9ca3af" }} />
        <Typography
          sx={{ fontSize: "0.68rem", color: "#9ca3af", fontWeight: 600 }}
        >
          Select a class first to unlock
        </Typography>
      </Stack>
    )}
  </MotionBox>
);

const SelectorPanel = ({
  data,
  selectedClass,
  setSelectedClass,
  selectedSubject,
  setSelectedSubject,
  selectedStudent,
  setSelectedStudent,
  subjects,
  students,
  loadingDependents,
  filters,
  navigate,
}) => {
  const classSelected = !!selectedClass;

  const buildQuery = () => {
    const params = [];
    if (filters.term) params.push(`term=${filters.term}`);
    if (filters.session) params.push(`session=${filters.session}`);
    return params.length ? `?${params.join("&")}` : "";
  };

  const handleClassChange = (val) => {
    setSelectedClass(val);
    setSelectedSubject("");
    setSelectedStudent("");
  };

  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        mb: 3,
        borderRadius: "24px",
        overflow: "hidden",
        position: "relative",
        ...glassMixin,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background:
            "linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%)",
        }}
      />

      <CardContent sx={{ p: "24px !important" }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
              color: "#111827",
              fontSize: "1rem",
            }}
          >
            Drill-Down Analysis
          </Typography>
          <Chip
            label="Interactive"
            size="small"
            sx={{
              background:
                "linear-gradient(90deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1))",
              color: "#6366f1",
              fontWeight: 700,
              fontSize: "0.66rem",
              border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: "8px",
            }}
          />
        </Stack>

        <Stack spacing={2}>
          <SelectorSection
            icon={<ClassRoundedIcon />}
            label="Class"
            description="Select a class to enable subject & student filters"
            value={selectedClass}
            onChange={handleClassChange}
            options={data?.classes || []}
            disabled={false}
            color="#8b5cf6"
            lightColor="rgba(139,92,246,0.12)"
            gradient="linear-gradient(90deg, #8b5cf6, #6366f1)"
            placeholder="Select Class"
            loading={false}
            onNavigate={() =>
              navigate(`/analysis/class/${selectedClass}${buildQuery()}`)
            }
            navigateLabel="Class Analysis"
            navigateDisabled={!selectedClass}
          />

          <SelectorSection
            icon={<MenuBookRoundedIcon />}
            label="Subject"
            description="Subject performance within the selected class"
            value={selectedSubject}
            onChange={setSelectedSubject}
            options={subjects}
            disabled={!classSelected}
            color="#10b981"
            lightColor="rgba(16,185,129,0.12)"
            gradient="linear-gradient(90deg, #10b981, #34d399)"
            placeholder="Select Subject"
            loading={loadingDependents}
            onNavigate={() =>
              navigate(`/analysis/subject/${selectedSubject}${buildQuery()}`)
            }
            navigateLabel="Subject Analysis"
            navigateDisabled={!selectedSubject}
          />

          <SelectorSection
            icon={<PersonRoundedIcon />}
            label="Student"
            description="Individual student performance"
            value={selectedStudent}
            onChange={setSelectedStudent}
            options={students}
            disabled={!classSelected}
            color="#6366f1"
            lightColor="rgba(99,102,241,0.12)"
            gradient="linear-gradient(90deg, #6366f1, #818cf8)"
            placeholder="Select Student"
            loading={loadingDependents}
            onNavigate={() =>
              navigate(`/analysis/student/${selectedStudent}${buildQuery()}`)
            }
            navigateLabel="Student Analysis"
            navigateDisabled={!selectedStudent}
          />
        </Stack>
      </CardContent>
    </MotionCard>
  );
};

export default SelectorPanel;
